import type { TypedSupabaseClient } from '../client';
import type { TablesInsert, TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IStockRepository,
  Warehouse,
  CreateWarehouseInput,
  UpdateWarehouseInput,
  StockMovement,
  CreateStockMovementInput,
  ArticleStock,
  Inventory,
  InventoryLine,
  CreateInventoryInput,
  StockExitDocument,
  StockExitDocumentLine,
  CreateStockExitDocumentInput,
  StockVehicleDocumentType,
} from '../../../interfaces/src';
import { ensureLowStockNotificationsForArticles } from './low-stock-notifications';

const LEGACY_STOCK_DOC_META_PREFIX = '[ERP_STOCK_DOCUMENT_META:';
const LEGACY_STOCK_DOC_META_SUFFIX = ']';

interface LegacyStockDocumentMeta {
  documentType?: StockVehicleDocumentType;
  parentDocumentId?: string | null;
}

function isStockVehicleDocumentType(value: unknown): value is StockVehicleDocumentType {
  return value === 'exit' || value === 'entry';
}

function parseLegacyStockDocumentNotes(notes: string | null | undefined): {
  meta: LegacyStockDocumentMeta;
  notes: string | null;
} {
  if (!notes) return { meta: {}, notes: notes ?? null };
  const start = notes.indexOf(LEGACY_STOCK_DOC_META_PREFIX);
  if (start === -1) return { meta: {}, notes };
  const bodyStart = start + LEGACY_STOCK_DOC_META_PREFIX.length;
  const end = notes.indexOf(LEGACY_STOCK_DOC_META_SUFFIX, bodyStart);
  if (end === -1) return { meta: {}, notes };

  const rawMeta = notes.slice(bodyStart, end);
  const cleanedNotes = `${notes.slice(0, start)}${notes.slice(end + LEGACY_STOCK_DOC_META_SUFFIX.length)}`.trim();
  try {
    const parsed = JSON.parse(rawMeta) as Record<string, unknown>;
    const documentType = isStockVehicleDocumentType(parsed['documentType']) ? parsed['documentType'] : undefined;
    const parentDocumentId = typeof parsed['parentDocumentId'] === 'string' ? parsed['parentDocumentId'] : null;
    return {
      meta: { documentType, parentDocumentId },
      notes: cleanedNotes || null,
    };
  } catch {
    return { meta: {}, notes };
  }
}

function withLegacyStockDocumentMeta(
  notes: string | undefined,
  meta: Required<Pick<LegacyStockDocumentMeta, 'documentType'>> & Pick<LegacyStockDocumentMeta, 'parentDocumentId'>,
): string {
  const marker = `${LEGACY_STOCK_DOC_META_PREFIX}${JSON.stringify(meta)}${LEGACY_STOCK_DOC_META_SUFFIX}`;
  const cleanNotes = notes?.trim();
  return cleanNotes ? `${marker}\n${cleanNotes}` : marker;
}

function isMissingSchemaColumnError(error: { message: string }): boolean {
  return error.message.includes('Could not find the') && error.message.includes('column');
}

// ── Mappers ──────────────────────────────────────────────────────────

function mapWarehouse(row: Record<string, unknown>): Warehouse {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    isDefault: (row['is_default'] as boolean) ?? false,
  };
}

function mapMovement(row: Record<string, unknown>): StockMovement {
  return {
    id: row['id'] as string,
    articleId: row['article_id'] as string,
    warehouseId: row['warehouse_id'] as string,
    movementType: row['movement_type'] as StockMovement['movementType'],
    quantity: row['quantity'] as number,
    referenceType: (row['reference_type'] as string) ?? null,
    referenceId: (row['reference_id'] as string) ?? null,
    movementDate: row['movement_date'] as string,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
  };
}

function mapInventory(row: Record<string, unknown>): Inventory {
  const lines = row['inventory_lines'] as Record<string, unknown>[] | undefined;
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    warehouseId: row['warehouse_id'] as string,
    inventoryDate: row['inventory_date'] as string,
    isValidated: (row['is_validated'] as boolean) ?? false,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    lines: lines ? lines.map(mapInventoryLine) : undefined,
  };
}

function mapInventoryLine(row: Record<string, unknown>): InventoryLine {
  return {
    id: row['id'] as string,
    inventoryId: row['inventory_id'] as string,
    articleId: row['article_id'] as string,
    theoreticalQty: row['theoretical_qty'] as number,
    countedQty: row['counted_qty'] as number,
    difference: row['difference'] as number,
  };
}

function mapStockExitDocument(row: Record<string, unknown>): StockExitDocument {
  const lines = row['stock_exit_document_lines'] as Record<string, unknown>[] | undefined;
  const legacyNotes = parseLegacyStockDocumentNotes((row['notes'] as string) ?? null);
  const number = row['number'] as string;
  const rowDocumentType = row['document_type'];
  const documentType = legacyNotes.meta.documentType
    ?? (isStockVehicleDocumentType(rowDocumentType) ? rowDocumentType : undefined)
    ?? (number.startsWith('BE-') ? 'entry' : 'exit');
  return {
    id: row['id'] as string,
    documentType,
    number,
    carRegistrationNumber: row['car_registration_number'] as string,
    exitDate: row['exit_date'] as string,
    parentDocumentId: legacyNotes.meta.parentDocumentId ?? (row['parent_document_id'] as string) ?? null,
    notes: legacyNotes.notes,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines ? lines.map(mapStockExitLine) : undefined,
  };
}

function mapStockExitLine(row: Record<string, unknown>): StockExitDocumentLine {
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: row['article_id'] as string,
    quantity: row['quantity'] as number,
    notes: (row['notes'] as string) ?? null,
  };
}

// ── Repository ───────────────────────────────────────────────────────

export class SupabaseStockRepository implements IStockRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private async notifyLowStock(articleIds: string[]): Promise<void> {
    try {
      await ensureLowStockNotificationsForArticles(this.supabase, articleIds);
    } catch (error) {
      console.warn('Low-stock notification failed:', error);
    }
  }

  // ── Warehouses ─────────────────────────────────────────────────

  async listWarehouses(): Promise<Warehouse[]> {
    const { data, error } = await this.supabase
      .from('warehouses')
      .select('*')
      .order('name');
    if (error) throw error;
    return (data ?? []).map(mapWarehouse);
  }

  async createWarehouse(input: CreateWarehouseInput): Promise<Warehouse> {
    const { data, error } = await this.supabase
      .from('warehouses')
      .insert({
        code: input.code,
        name: input.name,
        is_default: input.isDefault ?? false,
      })
      .select()
      .single();
    if (error) throw error;
    return mapWarehouse(data);
  }

  async updateWarehouse(id: string, input: UpdateWarehouseInput): Promise<Warehouse> {
    const mapped: TablesUpdate<'warehouses'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.isDefault !== undefined) mapped['is_default'] = input.isDefault;

    const { data, error } = await this.supabase
      .from('warehouses')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapWarehouse(data);
  }

  async deleteWarehouse(id: string): Promise<void> {
    const { error } = await this.supabase.from('warehouses').delete().eq('id', id);
    if (error) throw error;
  }

  // ── Stock Movements ────────────────────────────────────────────

  async listMovements(articleId?: string, warehouseId?: string): Promise<StockMovement[]> {
    let query = this.supabase.from('stock_movements').select('*');
    if (articleId) {
      query = query.eq('article_id', articleId);
    }
    if (warehouseId) {
      query = query.eq('warehouse_id', warehouseId);
    }
    const { data, error } = await query.order('movement_date', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapMovement);
  }

  async createMovement(input: CreateStockMovementInput): Promise<StockMovement> {
    const { data, error } = await this.supabase
      .from('stock_movements')
      .insert({
        article_id: input.articleId,
        warehouse_id: input.warehouseId,
        movement_type: input.movementType,
        quantity: input.quantity,
        reference_type: input.referenceType,
        reference_id: input.referenceId,
        movement_date: input.movementDate,
        notes: input.notes,
      })
      .select()
      .single();
    if (error) throw error;
    if (input.referenceType !== 'transfer') {
      await this.notifyLowStock([input.articleId]);
    }
    return mapMovement(data);
  }

  // ── Stock Calculation ──────────────────────────────────────────

  async getArticleStock(articleId: string, warehouseId?: string): Promise<ArticleStock[]> {
    let query = this.supabase
      .from('stock_movements')
      .select('*')
      .eq('article_id', articleId);
    if (warehouseId) {
      query = query.eq('warehouse_id', warehouseId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return aggregateStock(data ?? []);
  }

  async getAllStock(warehouseId?: string): Promise<ArticleStock[]> {
    let query = this.supabase.from('stock_movements').select('*');
    if (warehouseId) {
      query = query.eq('warehouse_id', warehouseId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return aggregateStock(data ?? []);
  }

  // ── Inventories ────────────────────────────────────────────────

  async listInventories(warehouseId?: string): Promise<Inventory[]> {
    let query = this.supabase.from('inventories').select('*');
    if (warehouseId) {
      query = query.eq('warehouse_id', warehouseId);
    }
    const { data, error } = await query.order('inventory_date', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapInventory);
  }

  async getInventoryById(id: string): Promise<Inventory | null> {
    const { data, error } = await this.supabase
      .from('inventories')
      .select('*, inventory_lines(*)')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapInventory(data);
  }

  async createInventory(input: CreateInventoryInput): Promise<Inventory> {
    // 1. Get current theoretical stock for the warehouse
    const currentStock = await this.getAllStock(input.warehouseId);
    const stockMap = new Map<string, number>();
    for (const s of currentStock) {
      stockMap.set(s.articleId, s.quantity);
    }

    // 2. Allocate a sequential inventory number
    const { data: seqValue, error: seqError } = await this.supabase.rpc(
      'get_next_sequence_value',
      { seq_name: 'seq_inventory' },
    );
    if (seqError) throw seqError;
    const year = new Date(input.inventoryDate).getFullYear();
    const number = `INV-${year}-${String(seqValue).padStart(4, '0')}`;

    // 3. Insert the inventory header
    const inventoryInsert: TablesInsert<'inventories'> = {
      number,
      warehouse_id: input.warehouseId,
      inventory_date: input.inventoryDate,
      notes: input.notes,
      is_validated: false,
    };
    const { data: inv, error: invError } = await this.supabase
      .from('inventories')
      .insert(inventoryInsert)
      .select()
      .single();
    if (invError) throw invError;

    // 4. Insert inventory lines with theoretical qty from current stock
    const linesToInsert: TablesInsert<'inventory_lines'>[] = input.lines.map((line) => {
      const theoreticalQty = stockMap.get(line.articleId) ?? 0;
      return {
        inventory_id: inv.id,
        article_id: line.articleId,
        theoretical_qty: theoreticalQty,
        counted_qty: line.countedQty,
      };
    });

    if (linesToInsert.length > 0) {
      const { error: linesError } = await this.supabase
        .from('inventory_lines')
        .insert(linesToInsert);
      if (linesError) throw linesError;
    }

    // 5. Return the full inventory with lines
    const created = await this.getInventoryById(inv.id);
    if (!created) throw new Error('Inventory not found after creation');
    return created;
  }

  async validateInventory(id: string): Promise<Inventory> {
    // 1. Get the inventory with its lines
    const inventory = await this.getInventoryById(id);
    if (!inventory) throw new Error('Inventory not found');
    if (inventory.isValidated) throw new Error('Inventory already validated');
    if (!inventory.lines || inventory.lines.length === 0) {
      throw new Error('Inventory has no lines');
    }

    // 2. Create adjustment movements for lines with a non-zero difference
    const adjustmentMovements = inventory.lines
      .filter((line) => line.difference !== 0)
      .map((line) => ({
        article_id: line.articleId,
        warehouse_id: inventory.warehouseId,
        movement_type: 'adjustment' as const,
        quantity: line.difference,
        reference_type: 'inventory',
        reference_id: inventory.id,
        movement_date: inventory.inventoryDate,
        notes: `Ajustement inventaire ${inventory.number}`,
      }));

    if (adjustmentMovements.length > 0) {
      const { error: movError } = await this.supabase
        .from('stock_movements')
        .insert(adjustmentMovements);
      if (movError) throw movError;
      await this.notifyLowStock(adjustmentMovements.map((movement) => movement.article_id));
    }

    // 3. Mark inventory as validated
    const { data, error } = await this.supabase
      .from('inventories')
      .update({ is_validated: true })
      .eq('id', id)
      .select('*, inventory_lines(*)')
      .single();
    if (error) throw error;
    return mapInventory(data);
  }

  async listStockExitDocuments(documentType?: StockVehicleDocumentType): Promise<StockExitDocument[]> {
    type SbAny = { from: (table: string) => unknown };
    type StockExitQuery = {
      order: (col: string, opts: { ascending: boolean }) => Promise<{
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      }>;
    };
    const sb = this.supabase as unknown as SbAny;
    const query = (sb.from('stock_exit_documents') as {
      select: (cols: string) => StockExitQuery;
    })
      .select('*, stock_exit_document_lines(*)');
    const { data, error } = await query.order('exit_date', { ascending: false });
    if (error) throw new Error(error.message);
    const docs = (data ?? []).map(mapStockExitDocument);
    return documentType ? docs.filter((doc) => doc.documentType === documentType) : docs;
  }

  async getStockExitDocumentById(id: string): Promise<StockExitDocument | null> {
    type SbAny = { from: (table: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const { data, error } = await (sb.from('stock_exit_documents') as {
      select: (cols: string) => {
        eq: (col: string, val: string) => {
          single: () => Promise<{
            data: Record<string, unknown> | null;
            error: { code?: string; message: string } | null;
          }>;
        };
      };
    })
      .select('*, stock_exit_document_lines(*)')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    return data ? mapStockExitDocument(data) : null;
  }

  async createStockExitDocument(input: CreateStockExitDocumentInput): Promise<StockExitDocument> {
    const documentType = input.documentType ?? 'exit';
    const { data: seqValue, error: seqError } = await this.supabase.rpc(
      'get_next_sequence_value',
      { seq_name: documentType === 'entry' ? 'seq_stock_entry' : 'seq_stock_exit' },
    );
    if (seqError) throw seqError;
    const year = new Date(input.exitDate).getFullYear();
    const number = `${documentType === 'entry' ? 'BE' : 'BS'}-${year}-${String(seqValue).padStart(4, '0')}`;

    type SbAny = { from: (table: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const insertDocument = (row: Record<string, unknown>) => (sb.from('stock_exit_documents') as {
      insert: (row: Record<string, unknown>) => {
        select: () => {
          single: () => Promise<{
            data: { id: string } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).insert(row).select().single();

    let docRes = await insertDocument({
      document_type: documentType,
      number,
      car_registration_number: input.carRegistrationNumber,
      exit_date: input.exitDate,
      parent_document_id: input.parentDocumentId ?? null,
      notes: input.notes,
    });
    if (docRes.error && isMissingSchemaColumnError(docRes.error)) {
      docRes = await insertDocument({
        number,
        car_registration_number: input.carRegistrationNumber,
        exit_date: input.exitDate,
        notes: documentType === 'entry'
          ? withLegacyStockDocumentMeta(input.notes, {
            documentType,
            parentDocumentId: input.parentDocumentId ?? null,
          })
          : input.notes,
      });
    }
    if (docRes.error) throw new Error(docRes.error.message);
    if (!docRes.data) throw new Error('stock_exit_create_returned_no_row');

    const lines = input.lines.map((line, idx) => ({
      document_id: docRes.data!.id,
      line_number: idx + 1,
      article_id: line.articleId,
      quantity: line.quantity,
      notes: line.notes,
    }));

    if (lines.length > 0) {
      const linesRes = await (sb.from('stock_exit_document_lines') as {
        insert: (rows: Record<string, unknown>[]) => Promise<{ error: { message: string } | null }>;
      }).insert(lines);
      if (linesRes.error) throw new Error(linesRes.error.message);
    }

    const created = await this.getStockExitDocumentById(docRes.data.id);
    if (!created) throw new Error('Stock exit document not found after creation');
    return created;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Aggregates stock movements client-side.
 * in → +quantity, out → -quantity, adjustment → quantity as-is (can be + or -)
 */
function aggregateStock(rows: Record<string, unknown>[]): ArticleStock[] {
  const map = new Map<string, number>();

  for (const row of rows) {
    const articleId = row['article_id'] as string;
    const warehouseId = row['warehouse_id'] as string;
    const movementType = row['movement_type'] as string;
    const quantity = row['quantity'] as number;
    const key = `${articleId}::${warehouseId}`;

    const current = map.get(key) ?? 0;
    if (movementType === 'in') {
      map.set(key, current + quantity);
    } else if (movementType === 'out') {
      map.set(key, current - quantity);
    } else {
      // adjustment: quantity is the delta (positive or negative)
      map.set(key, current + quantity);
    }
  }

  const result: ArticleStock[] = [];
  for (const [key, quantity] of map.entries()) {
    const [articleId, warehouseId] = key.split('::');
    result.push({ articleId: articleId!, warehouseId: warehouseId!, quantity });
  }
  return result;
}
